import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  ArrowLeft, Pencil, Mail, Phone, MapPin,
  Building2, Briefcase, Tag, Calendar, Clock,
  User,
} from 'lucide-react'
import { useCliente, useHistorial } from '../hooks/useBdClientes'

export function ClienteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const clienteId = Number(id)

  const { data: cliente, isLoading } = useCliente(clienteId)
  const { data: historial } = useHistorial(clienteId, { page: 1, page_size: 5 })

  useDocumentTitle(cliente ? `${cliente.nombre_contacto} - BD Clientes` : 'BD Clientes')

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    )
  }

  if (!cliente) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Cliente no encontrado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/bd-clientes')}>
          Volver al listado
        </Button>
      </div>
    )
  }

  const estadoConfig: Record<string, { label: string; className: string }> = {
    activo: { label: 'Activo', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    inactivo: { label: 'Inactivo', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    prospecto: { label: 'Por validar', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  }
  const estado = estadoConfig[cliente.estado] || { label: cliente.estado, className: '' }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/bd-clientes')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <User className="h-5 w-5 text-teal-500" />
              {cliente.nombre_contacto}
            </h1>
            <Badge variant="outline" className={estado.className}>{estado.label}</Badge>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/bd-clientes/${cliente.id}/editar`)}
          className="bg-teal-800 hover:bg-teal-900 text-white"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Información básica */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Información del Cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Nombre</p>
                <p className="font-medium">{cliente.nombre}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Apellido</p>
                <p className="font-medium">{cliente.apellido}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{cliente.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{cliente.telefono_celular}</span>
                {cliente.telefono_fijo && (
                  <span className="text-muted-foreground">/ {cliente.telefono_fijo}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{cliente.direccion}</span>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Laboratorio</p>
                  <p className="text-sm font-medium">{cliente.laboratorio_nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Cargo</p>
                  <p className="text-sm font-medium">{cliente.cargo_nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Línea / Marca</p>
                  <p className="text-sm font-medium">{cliente.linea_nombre} / {cliente.marca}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">País</p>
                  <p className="text-sm font-medium">{cliente.pais_nombre}</p>
                </div>
              </div>
            </div>

            {(cliente.cumpleanos_dia || cliente.cumpleanos_mes) && (
              <>
                <Separator />
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Cumpleaños</p>
                    <p className="text-sm font-medium">
                      {cliente.cumpleanos_formato}
                      {cliente.edad !== null && ` (${cliente.edad} años)`}
                    </p>
                  </div>
                </div>
              </>
            )}

            {cliente.comentarios && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Comentarios</p>
                  <p className="text-sm mt-1">{cliente.comentarios}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Historial reciente */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Historial Reciente</CardTitle>
          </CardHeader>
          <CardContent>
            {historial && historial.results.length > 0 ? (
              <div className="space-y-4">
                {historial.results.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="rounded-full bg-teal-500/10 p-1.5">
                        <Clock className="h-3 w-3 text-teal-500" />
                      </div>
                      <div className="flex-1 w-px bg-gray-200 dark:bg-gray-700 mt-1" />
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="text-sm font-medium">{item.accion_display}</p>
                      <p className="text-xs text-muted-foreground">{item.descripcion}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(item.fecha_creacion).toLocaleString('es-EC')}
                        {item.usuario_nombre && ` · ${item.usuario_nombre}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Sin historial registrado
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
